import { Cable, createCable } from '@anycable/web'
import { getCompanyDomain } from '@/common/services/companyDomainGetSet'
import { useAuth } from '@/common/stores/auth'
import { useCableStatusStore } from '@/common/stores/cableStatus'

// Builds the cable URL off VITE_BASE_URL, splicing in the company subdomain the same way
// usePrivateAxios rewrites the Axios baseURL (multi-tenant by subdomain).
const buildCableUrl = (): string | null => {
  const base = import.meta.env.VITE_BASE_URL as string | undefined
  if (!base) return null

  let origin = base
  const companyDomain = getCompanyDomain()

  if (companyDomain) {
    const [, rest] = base.split('://')
    const currentDomain = rest?.split('/')[0]

    if (currentDomain && !currentDomain.startsWith(`${companyDomain}.`)) {
      origin = base.replace('://', `://${companyDomain}.`)
    }
  }

  return `${origin.replace('https://', 'wss://').replace('http://', 'ws://')}/cable`
}

// The backend authenticates the cable via a `Bearer.<jwt>` WebSocket subprotocol
// (ApplicationCable::Connection#extract_token_from_subprotocol). AnyCable's built-in auth uses a
// different prefix, so we inject ours by wrapping the WebSocket the transport instantiates.
const authWebSocket = (jwt: string) =>
  class extends WebSocket {
    constructor(url: string | URL, protocols?: string | string[]) {
      const base = protocols == null ? [] : Array.isArray(protocols) ? protocols : [protocols]
      super(url, [`Bearer.${jwt}`, ...base])
    }
  }

let cable: Cable | null = null
let cableKey = ''

// A single shared connection for the whole app; every channel subscription is multiplexed over
// it, and AnyCable owns reconnection. Recreated only when the tenant (URL) or the JWT changes
// (re-login / tenant switch).
export const getCable = (): Cable | null => {
  const url = buildCableUrl()
  const token = useAuth.getState().accessToken
  if (!url || !token) return null

  const jwt = token.replace(/^Bearer\s+/i, '')
  const key = `${url}::${jwt}`
  if (cable && cableKey === key) return cable

  if (cable) cable.disconnect()

  const { setStatus } = useCableStatusStore.getState()
  const next = createCable(url, {
    protocol: 'actioncable-v1-json',
    websocketImplementation: authWebSocket(jwt),
  })

  next.on('connect', () => setStatus('connected'))
  next.on('disconnect', () => setStatus('disconnected'))
  next.on('close', () => setStatus('disconnected'))
  setStatus('connecting')

  cable = next
  cableKey = key
  return cable
}
